import type { CSSProperties } from 'react';
import { usd, usdRate, type AdminStats } from '../../lib/admin';
import { compactNumber, formatDuration } from '../../lib/admin-format';

function Kpi({ label, value, accent, note }: { label: string; value: string; accent?: string; note?: string }) {
  const accentStyle = accent ? ({ '--kpi-accent': accent } as CSSProperties) : undefined;
  return (
    <div className="bg-[var(--kpi-accent,var(--naia-card))] border border-border rounded-[12px] p-[8px_10px] shadow-card" style={accentStyle}>
      <div className="text-[9px] font-black tracking-[0.9px] text-muted-foreground">{label}</div>
      <div className="text-[18px] font-extrabold mt-[2px]">{value}</div>
      {note ? <div className="text-[9px] mt-[2px] text-[var(--naia-orange)]">{note}</div> : null}
    </div>
  );
}

/* Admin dashboard KPI grid (costs + token/audio totals + averages). */
/* Presentational — the summary stats come from the parent. Extracted from */
/* Admin.tsx (NAI-207). */
export function KpiGrid({ stats }: { stats: AdminStats | null }) {
  return (
    <section className="mt-[14px] grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2">
      <Kpi
        label="Coût total"
        value={stats ? usd(stats.totalUsd) : '—'}
        accent="var(--naia-info-bg)"
        note={
          stats && stats.unpricedRecords > 0
            ? `${stats.unpricedRecords} appel${stats.unpricedRecords > 1 ? 's' : ''} non tarifé${stats.unpricedRecords > 1 ? 's' : ''}`
            : undefined
        }
      />
      <Kpi label="Moyenne / conv" value={stats ? usd(stats.avgCostPerThread) : '—'} accent="var(--naia-card-alt)" />
      <Kpi
        label="$/min vocal"
        value={stats ? `${usdRate(stats.avgUsdPerVoiceMinute)}/min` : '—'}
        accent="var(--naia-card-alt)"
      />
      <Kpi label="Coût texte" value={stats ? usd(stats.textUsd) : '—'} />
      <Kpi label="Coût vocal" value={stats ? usd(stats.voiceUsd) : '—'} />
      <Kpi label="Convs payantes" value={stats ? String(stats.threadsWithUsage) : '—'} />
      <Kpi label="Utilisateurs" value={stats ? String(stats.uniqueUsers) : '—'} />
      <Kpi label="Vocal cumulé" value={stats ? formatDuration(stats.voiceSeconds) : '—'} />
      <Kpi
        label="Tokens texte total in/out"
        value={stats ? `${compactNumber(stats.inputTokens)} / ${compactNumber(stats.outputTokens)}` : '—'}
      />
      <Kpi
        label="Tokens texte moy / conv"
        value={
          stats
            ? `${compactNumber(Math.round(stats.avgTextTokensInPerThread))} / ${compactNumber(Math.round(stats.avgTextTokensOutPerThread))}`
            : '—'
        }
      />
      <Kpi
        label="Tokens audio total in/out"
        value={stats ? `${compactNumber(stats.audioInputTokens)} / ${compactNumber(stats.audioOutputTokens)}` : '—'}
      />
      <Kpi
        label="Tokens audio moy / conv"
        value={
          stats
            ? `${compactNumber(Math.round(stats.avgAudioTokensInPerThread))} / ${compactNumber(Math.round(stats.avgAudioTokensOutPerThread))}`
            : '—'
        }
      />
    </section>
  );
}
